import { gql, useLazyQuery } from '@apollo/client'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import Close from '@mui/icons-material/Close'
import { Box, Button, IconButton } from '@mui/material'
import Fab from '@mui/material/Fab'
import Slide from '@mui/material/Slide'
import I18n from 'i18n-js'
import { memo, useMemo, useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import Slider from 'react-slick'
import { makeStyles } from 'tss-react/mui'

import analytics from '@src/analytics'
import useBillboardHiddenState from '@src/components/content/billboards/useBillboardHiddenState'
import { useAutomations } from '@src/lib/useAutomations'
import InstagramIcon from '@src/svgs/legacy/instagram.svg?react'
import PromoboxxIcon from '@src/svgs/legacy/pbxx_logo_colored.svg?react'

import replaceVariables from '../../lib/replaceVariables'
import { useAllFeatureFlags, useFeatureFlag } from '../../lib/useFeatureFlag'
import {
  currentRetailerAccountAndBrandSlugSelector,
  currentRetailerAccountSelector,
} from '../../stores/app/selectors'
import { brandsBySlugByRetailerAccountSlugSelector } from '../../stores/brands/selectors'
import manageBrandsPaths from '../brands/manageBrandsPaths'

import BaseBillboard from './BaseBillboard'

const useStyles = makeStyles()((theme, _params, classes) => ({
  igBillboardTextContainer: {
    'padding': theme.spacing(8),
    'flexGrow': 1,
    ...theme.typography.h5,
    [theme.breakpoints.up('md')]: {
      ...theme.typography.h4,
    },
    '& em': {
      color: theme.palette.secondary.main,
      fontStyle: 'normal',
    },
  },
  igBillboardImageSpacer: {
    marginRight: theme.spacing(4),
  },
  slider: {
    'backgroundColor': theme.palette.common.white,

    // TODO Get theme shadows looking like our app
    'boxShadow': '0 2px 14px 0 rgba(0, 0, 0, 0.1)',

    '& .slick-next, & .slick-prev': {
      // Don't rely on knowing width/height of next/prev buttons.
      width: 'auto',
      height: 'auto',

      // Support arrows over the slider.
      zIndex: theme.zIndex.appBar - 1,
      opacity: 0,
      [`.${classes.slider}:hover&`]: {
        'opacity': 0.6,
        '&:hover': {
          opacity: 1,
        },
      },
    },
    '& .slick-prev': {
      left: theme.spacing(1),
    },
    '& .slick-next': {
      right: theme.spacing(1),
    },

    // Remove react-slick's own buttons
    '& .slick-next:before, & .slick-prev:before': {
      display: 'none',
    },
  },
}))

const query = gql`
  query getBillboardsList($params: BillboardParams) {
    getBillboards(params: $params) {
      billboards {
        id
        body
        feature_flag
        image_id
        logo_id
        href
        name
        created_at
      }
    }
  }
`

/** @type {React.FC<import('react-slick').CustomArrowProps & {
 *  direction: 'next' | 'prev'
 * }>} */
const MuiSlickArrow = (props) => {
  const { className, onClick, style, direction } = props
  const icon = direction === 'next' ? <ArrowForwardIcon /> : <ArrowBackIcon />

  return (
    <div className={className} onClick={onClick} style={style}>
      <Fab size="small">{icon}</Fab>
    </div>
  )
}

/** @type {React.FC<{ setIsIGAutomationDialogOpen: React.Dispatch<React.SetStateAction<boolean>> }>} */
const Billboards = (props) => {
  const {
    isBillboardHidden,
    hideBillboard,
    clearAllBillboardHidden,
    areAllHidden,
  } = useBillboardHiddenState()

  const [isHidden, setIsHidden] = useState(areAllHidden())
  const toggleHidden = (event) => {
    setIsHidden((current) => !current)
  }
  const { forceUpdate, forceUpdateKey } = useForceUpdate()
  const { classes } = useStyles()
  const [runQuery, { data, error, loading }] = useLazyQuery(query)
  const currentSlugs = useSelector(currentRetailerAccountAndBrandSlugSelector)
  const brandsBySlug = useSelector(brandsBySlugByRetailerAccountSlugSelector)
  const allFeatureFlags = useAllFeatureFlags()
  const params = manageBrandsPaths.index.useParams()
  const brandIds = useMemo(() => {
    if (currentSlugs.brand === 'all') {
      return Object.values(brandsBySlug[currentSlugs.retailerAccount]).map(
        (brand) => brand.id,
      )
    }

    return [brandsBySlug[currentSlugs.retailerAccount][currentSlugs.brand].id]
  }, [brandsBySlug, currentSlugs])
  const user = useSelector((state) => state.auth.user)
  const retailerAccount = useSelector(currentRetailerAccountSelector)
  const variableReplacements = useMemo(() => {
    return {
      user,
      retailer: retailerAccount,
    }
  }, [user, retailerAccount])

  const { getAutomations, automations } = useAutomations(retailerAccount)

  const retailerIgAutomations =
    (automations &&
      automations.filter(
        (automation) =>
          automation.category === 'instagram' && automation.enabled,
      )) ||
    []

  const shouldShowIgAutomationBillboard =
    useFeatureFlag('ig-automation-billboard') && !retailerIgAutomations.length

  useEffect(() => {
    const now = new Date()

    runQuery({
      variables: {
        params: {
          brands: brandIds,
          starts_at_less_than: now,
          ends_at_greater_than: now,
          app_target: 'retailer',
          exclude_global_billboards: false,
          sort: 'priority_order:asc,starts_at:asc',
        },
      },
    })
    getAutomations(['retailer', 'automationable'])
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    // TODO This seems like a smell.
    // brandIds is memoized, its dependencies are a bunch of selectors (so also
    // memoized)
    // Yet somewhere along the way, something is returning a new object.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    brandIds.join(','),
    getAutomations,
    params.retailerAccountSlug,
  ])

  // Do nothing if loading, cause if we try to show the default banner while
  // loading, it will only appear for a fraction of a second before being
  // replaced.
  if (loading) {
    return null
  }

  let areAnyHidden = false

  let filteredBillboards =
    data && data.getBillboards
      ? data.getBillboards.billboards.filter((billboard) => {
          if (isBillboardHidden(billboard.id)) {
            areAnyHidden = true
            return false
          }

          if (!billboard.feature_flag) {
            return true
          }

          return allFeatureFlags[billboard.feature_flag] === true
        })
      : []

  if (shouldShowIgAutomationBillboard) {
    filteredBillboards = [
      {
        id: 'ig-automation-billboard',
        name: 'Special: IG Automation',
      },
      ...filteredBillboards,
    ]
  }

  /**
   * @param {number} index
   * @param {'Click' | 'View'} event
   **/
  const trackBillboard = (index, event) => {
    const billboard = filteredBillboards[index]

    if (!billboard) {
      return
    }

    analytics.track({
      event,
      type: 'Billboard',
      data: {
        name: billboard.name,
        brandId: billboard.brand_id,
      },
    })
  }

  const handleHide = (index) => {
    const billboard = filteredBillboards[index]

    if (!billboard) {
      return
    }

    hideBillboard(billboard.id)
    forceUpdate()
    analytics.track({
      event: 'Click',
      type: 'Hide Billboard',
      data: {
        name: billboard.name,
        brandId: billboard.brand_id,
      },
    })
  }

  if ((filteredBillboards.length === 0 && !areAnyHidden) || !data || error) {
    return null
  }

  return (
    <div>
      <Slider
        afterChange={(index) => {
          trackBillboard(index, 'View')
        }}
        arrows
        autoplay
        autoplaySpeed={5000}
        className={classes.slider}
        dots
        nextArrow={<MuiSlickArrow direction="next" />}
        onInit={() => trackBillboard(0, 'View')}
        pauseOnHover
        prevArrow={<MuiSlickArrow direction="prev" />}
        key={forceUpdateKey}
      >
        {/* Temporary IG Automation billboard */}
        {filteredBillboards.map((billboard, index) => {
          const createdAt = new Date(billboard.created_at).getTime()
          const threshold = new Date('2021-10-15T00:00:00.000000Z').getTime()
          let fixedSize = false
          if (allFeatureFlags['billboard-force-fixed-size'] === true) {
            fixedSize = createdAt >= threshold //When the feature flag is TRUE, only force *new* billboards.
          }
          if (billboard.name === 'Special: IG Automation') {
            if (isHidden) {
              isBillboardHidden('ig-automation-billboard')
              areAnyHidden = true
              // if IG billboard is hidden, return a blank billboard
              return null
            } else
              return (
                <div key={billboard.id}>
                  <Box display="flex" justifyContent="right">
                    <IconButton size="small" onClick={toggleHidden}>
                      <Close fontSize="inherit" />
                    </IconButton>
                  </Box>
                  <div className={classes.igBillboardTextContainer}>
                    <PromoboxxIcon
                      height="1.6em"
                      className={classes.igBillboardImageSpacer}
                    />
                    <InstagramIcon height="1.6em" />
                    <div style={{ marginTop: '1em' }}>
                      {I18n.t('igAutoShare.comingSoon')}
                      <br />
                      <br />
                      <small>{I18n.t('igAutoShare.signUpNowMessage')}</small>
                    </div>
                    <Box display="flex" justifyContent="center" marginTop={3}>
                      <Button
                        onClick={() => {
                          trackBillboard(index, 'Click')
                          props.setIsIGAutomationDialogOpen(true)
                        }}
                        variant="contained"
                        color="primary"
                        size="large"
                      >
                        {I18n.t('enrollment.signUpNowButtonText')}
                      </Button>
                    </Box>
                  </div>
                </div>
              )
          }
          return (
            <BaseBillboard
              href={replaceVariables(billboard.href, variableReplacements)}
              image={billboard.image_id}
              fixedSize={fixedSize}
              key={billboard.id}
              logo={billboard.logo_id}
              name={billboard.name}
              onClick={() => trackBillboard(index, 'Click')}
              onHide={() => handleHide(index)}
              text={replaceVariables(billboard.body, variableReplacements)}
            />
          )
        })}
      </Slider>
      {(isHidden && (
        <Box
          display="flex"
          justifyContent="right"
          marginTop={5}
          marginBottom={5}
          overflow="hidden"
        >
          <Slide in={areAnyHidden} direction="down">
            <Button
              onClick={() => {
                clearAllBillboardHidden()
                forceUpdate()
                toggleHidden()
              }}
              variant="contained"
              color="primary"
              size="large"
            >
              {I18n.t('filters.showHiddenBillboards')}
            </Button>
          </Slide>
        </Box>
      )) ||
        (!isHidden && (
          <Box
            display="flex"
            justifyContent="right"
            marginTop={5}
            marginBottom={5}
            overflow="hidden"
          >
            <Slide in={areAnyHidden} direction="down">
              <Button
                onClick={() => {
                  clearAllBillboardHidden()
                  forceUpdate()
                }}
                variant="contained"
                color="primary"
                size="large"
              >
                {I18n.t('filters.showHiddenBillboards')}
              </Button>
            </Slide>
          </Box>
        ))}
    </div>
  )
}

const useForceUpdate = () => {
  const [forceUpdateKey, setForceUpdateKey] = useState(1)

  return {
    forceUpdateKey,
    forceUpdate: () => {
      setForceUpdateKey((i) => i + 1)
    },
  }
}

export default memo(Billboards)
